// IndexedDB access for the generic offline outbox queue.
// Record shape:
//   { id, type, method, url, payload, request, meta, dedupeKey,
//     status: "queued"|"syncing"|"failed", attempts, lastError, createdAt, updatedAt }
// Succeeded items are removed from the store (not retained).

import { idb } from "./db";
import { STORE_NAMES } from "./indexedDb.config";

const STORE = STORE_NAMES.OUTBOX;

const byCreatedAt = (a, b) => (a?.createdAt || 0) - (b?.createdAt || 0);

export const outboxDB = {
    async add(item) {
        await idb.put(STORE, item);
        return item;
    },

    async update(id, patch) {
        const current = await idb.get(STORE, id);
        if (!current) return null;
        const next = { ...current, ...patch, id };
        await idb.put(STORE, next);
        return next;
    },

    async remove(id) {
        await idb.delete(STORE, id);
    },

    async get(id) {
        return (await idb.get(STORE, id)) || null;
    },

    async getAll() {
        const all = (await idb.getAll(STORE)) || [];
        return all.sort(byCreatedAt);
    },

    async getByDedupeKey(dedupeKey) {
        if (!dedupeKey) return [];
        return (await idb.getAllFromIndex(STORE, "dedupeKey", dedupeKey)) || [];
    },

    // Items eligible for automatic replay (offline-created, awaiting network).
    async getReplayable() {
        const queued = (await idb.getAllFromIndex(STORE, "status", "queued")) || [];
        return queued.sort(byCreatedAt);
    },

    // Reclaims items orphaned in "syncing" (reload/crash/hung request) back to "queued".
    async requeueStuck() {
        const stuck = (await idb.getAllFromIndex(STORE, "status", "syncing")) || [];
        await Promise.all(stuck.map((item) => idb.put(STORE, { ...item, status: "queued", updatedAt: Date.now() })));
        return stuck.length;
    },

    async countPending() {
        const all = (await idb.getAll(STORE)) || [];
        return all.filter((i) => i.status !== "succeeded").length;
    },

    async clear() {
        await idb.clear(STORE);
    },
};
